/**
 * Permits, and the expiry reminders for permits and the plants' papers.
 *
 * Live only — these are the owner's admin edits and a small read; nothing is
 * queued in the outbox. With no signal the Permits tab says so, and the
 * reminders card simply does not draw.
 */

import { request } from './api';

export type DueStatus = 'expired' | 'red' | 'amber';

export interface PermitRenewal {
  previousExpiresOn: string;
  newExpiresOn: string;
  previousRefNo: string | null;
  newRefNo: string | null;
  renewedAt: string;
  by: string | null;
}

export interface PermitRow {
  id: string;
  name: string;
  authority: string | null;
  refNo: string | null;
  expiresOn: string;
  remindDays: number;
  notes: string | null;
  rowVersion: number;
  daysLeft: number;
  status: DueStatus | 'ok';
  renewals: PermitRenewal[];
}

export interface PermitInput {
  name: string;
  authority: string | null;
  refNo: string | null;
  expiresOn: string;
  remindDays: number;
  notes?: string | null;
}

export interface DueRow {
  kind: 'permit' | 'paper' | 'service';
  id: string;
  paper: 'licence' | 'insurance' | 'emission' | 'fitness' | 'service' | null;
  title: string;
  detail: string;
  expiresOn: string;
  daysLeft: number;
  status: DueStatus;
}

export const permitsApi = {
  list: () => request<{ today: string; canEdit: boolean; permits: PermitRow[] }>('/permits'),

  create: (body: PermitInput) => request<PermitRow>('/permits', { method: 'POST', body }),

  update: (id: string, body: Partial<PermitInput> & { rowVersion: number }) =>
    request<PermitRow>(`/permits/${id}`, { method: 'PATCH', body }),

  remove: (id: string) => request<void>(`/permits/${id}`, { method: 'DELETE' }),

  /** Everything due, worst first: permits inside their window, papers and services inside 30 days. */
  reminders: () => request<{ today: string; due: DueRow[]; expired: number }>('/reminders'),
};

/**
 * "expired 2 days ago", "expires today", "12 days" — what a reminder line says.
 * A service is not a paper that runs out: "overdue 2 days", "due today".
 */
export function whenLabel(daysLeft: number, kind: DueRow['kind'] = 'paper'): string {
  const n = (d: number) => `${d} ${d === 1 ? 'day' : 'days'}`;
  if (kind === 'service') {
    if (daysLeft < 0) return `overdue ${n(-daysLeft)}`;
    if (daysLeft === 0) return 'due today';
    return n(daysLeft);
  }
  if (daysLeft < 0) return `expired ${n(-daysLeft)} ago`;
  if (daysLeft === 0) return 'expires today';
  return n(daysLeft);
}

export const PAPER_LABEL: Record<NonNullable<DueRow['paper']>, string> = {
  licence: 'Revenue licence',
  insurance: 'Insurance',
  emission: 'Emission test',
  fitness: 'Fitness certificate',
  service: 'Service',
};
